import { Button } from '../ui/button'
import { Drawer, DrawerContent, DrawerHeader, DrawerClose } from '../ui/drawer'
import { Card, CardContent, CardTitle } from '../ui/card'
import { Avatar, AvatarFallback, AvatarImage, Badge, CryptoIcon } from '../ui'
import { Copy } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useMemo } from 'react'

export interface DepositWallet {
  currency: string
  address: string
}

interface DepositWalletsDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  companyName: string
  depositWallets: DepositWallet[]
  companyImageUrl?: string
  loading?: boolean
  onCopyAddress?: (address: string) => void
}

export function DepositWalletsDrawer({
  open,
  onOpenChange,
  companyName,
  depositWallets,
  companyImageUrl,
  loading = false,
  onCopyAddress,
}: DepositWalletsDrawerProps) {
  const handleCopyAddress = (address: string) => {
    if (onCopyAddress) {
      onCopyAddress(address)
    } else {
      // Default clipboard behavior
      navigator.clipboard.writeText(address)
    }
  }

  const sortedDepositWallets = useMemo(() => {
    return depositWallets.sort((a, b) => a.currency.localeCompare(b.currency))
  }, [depositWallets])

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className="px-6 md:px-15 relative">
          <div className="flex items-start justify-between pb-8 md:pt-4">
            <div className="flex flex-col items-start">
              <div className="flex items-center mb-8">
                <h3 className="text-lg font-bold">Deposit Wallets</h3>
              </div>
            </div>

            <Badge
              variant="outline"
              className="rounded-md px-3 py-1 absolute top-15 md:top-4 left-1/2 transform -translate-x-1/2"
            >
              <Avatar className="h-8 w-8 rounded-sm border border-card-border">
                <AvatarImage src={companyImageUrl} alt={companyName} />
                <AvatarFallback className="rounded-sm bg-gray-200">
                  <div className="rounded-sm bg-gray-200" />
                </AvatarFallback>
              </Avatar>
              <span className="ml-1 font-black text-lg">{companyName}</span>
            </Badge>

            <DrawerClose asChild>
              <Button variant="outline" size="sm">
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="py-3 px-6 pb-15 md:px-15">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading wallets...</div>
            </div>
          )}

          {!loading && depositWallets.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">
                No deposit wallets found for this company.
              </div>
            </div>
          )}

          {!loading && sortedDepositWallets.length > 0 && (
            <div className="space-y-6 flex flex-col md:flex-row gap-4 pb-4 md:pb-0">
              {sortedDepositWallets.map((wallet) => (
                <div key={wallet.currency} className="flex flex-col gap-4">
                  <Card className="relative">
                    <CardContent>
                      <div className="flex items-center gap-2 absolute top-0 left-6 -translate-y-1/2 bg-background rounded-full p-1">
                        <CryptoIcon symbol={wallet.currency} size="h-8 w-8" />
                        <div>
                          <CardTitle className="text-lg">{wallet.currency}</CardTitle>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1  p-2 rounded text-xs text-muted-foreground font-semibold break-all">
                          {wallet.address}
                        </div>
                        <Button
                          variant="main_action"
                          className={cn(
                            'text-xs  border border-card-border rounded-md px-2 py-1 w-fit cursor-pointer',
                            ' rounded-md gap-1.5 shrink-0',
                          )}
                          onClick={() => handleCopyAddress(wallet.address)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
